import { createServer, type IncomingMessage, type ServerResponse } from "http";
import { readFileSync, existsSync, writeFileSync, statSync } from "fs";
import { join, dirname, extname } from "path";
import { fileURLToPath } from "url";
import { execSync } from "child_process";
import { parsePlanFile } from "./parser.js";
import type { PlanMeta } from "./types.js";

const __dirname = dirname(fileURLToPath(import.meta.url));

const MIME_TYPES: Record<string, string> = {
  ".html": "text/html",
  ".js": "application/javascript",
  ".css": "text/css",
  ".json": "application/json",
  ".svg": "image/svg+xml",
  ".png": "image/png",
  ".ico": "image/x-icon",
  ".woff": "font/woff",
  ".woff2": "font/woff2",
};

function getUiDir(): string {
  const candidates = [
    join(__dirname, "ui"),
    join(__dirname, "..", "dist", "ui"),
  ];
  for (const p of candidates) {
    if (existsSync(join(p, "index.html"))) return p;
  }
  return join(__dirname, "ui");
}

function setCors(res: ServerResponse): void {
  res.setHeader("Access-Control-Allow-Origin", "*");
  res.setHeader("Access-Control-Allow-Methods", "GET, POST, OPTIONS");
  res.setHeader("Access-Control-Allow-Headers", "Content-Type");
}

function serveStaticFile(res: ServerResponse, filePath: string): boolean {
  if (!existsSync(filePath) || !statSync(filePath).isFile()) return false;
  const ext = extname(filePath);
  const contentType = MIME_TYPES[ext] ?? "application/octet-stream";
  const content = readFileSync(filePath);
  res.writeHead(200, { "Content-Type": contentType });
  res.end(content);
  return true;
}

export interface ServerOptions {
  planFile: string;
  port?: number;
  workspaceDir?: string;
}

function gitCmd(cwd: string, args: string): string | undefined {
  try {
    return execSync(`git ${args}`, { cwd, encoding: "utf-8" }).trim();
  } catch {
    return undefined;
  }
}

function getGitMeta(planFile: string, workspaceDir?: string): PlanMeta {
  const cwd = workspaceDir ?? dirname(planFile);
  const meta: PlanMeta = {};

  const remoteUrl = gitCmd(cwd, "remote get-url origin");
  if (remoteUrl) {
    const match = remoteUrl.match(/[/:]([^/]+\/[^/.]+?)(?:\.git)?$/);
    meta.repo = match?.[1] ?? remoteUrl;
  }

  meta.branch = gitCmd(cwd, "rev-parse --abbrev-ref HEAD");
  meta.sharedBy = gitCmd(cwd, "config user.name");

  return meta;
}

export function startServer(
  options: ServerOptions,
): Promise<{ port: number; url: string }> {
  return new Promise((resolve, reject) => {
    const plan = parsePlanFile(options.planFile);
    plan.meta = getGitMeta(options.planFile, options.workspaceDir);
    const uiDir = getUiDir();

    const server = createServer((req: IncomingMessage, res: ServerResponse) => {
      const url = new URL(req.url ?? "/", "http://localhost");

      setCors(res);

      if (req.method === "OPTIONS") {
        res.writeHead(204);
        res.end();
        return;
      }

      if (url.pathname === "/api/plan" && req.method === "GET") {
        res.writeHead(200, { "Content-Type": "application/json" });
        res.end(JSON.stringify(plan));
        return;
      }

      if (url.pathname === "/api/export-feedback" && req.method === "POST") {
        let body = "";
        req.on("data", (chunk: Buffer) => {
          body += chunk.toString();
        });
        req.on("end", () => {
          try {
            const { annotations, planName } = JSON.parse(body) as {
              annotations: unknown[];
              planName: string;
            };
            const feedbackPath = options.planFile.replace(
              ".plan.md",
              "-feedback.md",
            );
            const feedbackContent = buildFeedbackMarkdown(
              planName,
              annotations,
            );
            writeFileSync(feedbackPath, feedbackContent, "utf-8");
            res.writeHead(200, { "Content-Type": "application/json" });
            res.end(JSON.stringify({ path: feedbackPath }));
          } catch {
            res.writeHead(400);
            res.end(JSON.stringify({ error: "Invalid request" }));
          }
        });
        return;
      }

      const requestedFile = join(
        uiDir,
        url.pathname === "/" ? "index.html" : url.pathname,
      );
      if (serveStaticFile(res, requestedFile)) return;

      const indexPath = join(uiDir, "index.html");
      if (serveStaticFile(res, indexPath)) return;

      res.writeHead(404);
      res.end("UI not built. Run: npm run build:ui");
    });

    const desiredPort = options.port ?? 19450;

    server.listen(desiredPort, "127.0.0.1", () => {
      resolve({ port: desiredPort, url: `http://localhost:${desiredPort}` });
    });

    server.on("error", (err: NodeJS.ErrnoException) => {
      if (err.code === "EADDRINUSE") {
        server.listen(0, "127.0.0.1", () => {
          const addr = server.address() as { port: number };
          resolve({
            port: addr.port,
            url: `http://localhost:${addr.port}`,
          });
        });
      } else {
        reject(err);
      }
    });
  });
}

function buildFeedbackMarkdown(
  planName: string,
  annotations: unknown[],
): string {
  const lines: string[] = [
    `# Plan Feedback: ${planName}`,
    "",
    `_Generated by cursor-plan-preview on ${new Date().toISOString()}_`,
    "",
  ];

  if (!Array.isArray(annotations) || annotations.length === 0) {
    lines.push("No annotations provided.");
    return lines.join("\n");
  }

  lines.push(`## Annotations (${annotations.length})`);
  lines.push("");

  for (const ann of annotations) {
    const a = ann as Record<string, unknown>;
    const type = String(a["type"] ?? "COMMENT");
    const originalText = String(a["originalText"] ?? "");
    const text = String(a["text"] ?? "");

    lines.push(`### ${type}`);
    if (originalText) lines.push(`> "${originalText}"`);
    if (text) lines.push(`\n${text}`);
    lines.push("");
  }

  return lines.join("\n");
}
